// 앱이 보는 유일한 입구. capture 는 toAppError → classify → shouldDrop → fingerprint → toReportEvent 를
// 순서대로 돌려 어댑터에 넘긴다. 판정은 전부 여기(파이프라인)에서 끝나고 어댑터는 전달만 한다.

import type { AppError, ErrorFlow, ErrorMechanism } from './AppError'
import { classify } from './classify'
import { fingerprint } from './fingerprint'
import { CANCELED_CODE, type ReportingPolicy } from './policy'
import { shouldDrop } from './shouldDrop'
import { toAppError } from './toAppError'
import { type ReportEvent, toReportEvent } from './toReportEvent'

export type CaptureContext = { mechanism?: ErrorMechanism; flow?: ErrorFlow }

export type Breadcrumb = {
  category: 'ui.click' | 'navigation' | 'http' | 'chunk' | 'custom'
  message?: string
  data?: Record<string, string | number | boolean>
}

export type FwTag = 'fw.brand' | 'fw.platform' | 'fw.user.role' | 'fw.academy.id'
export type ReportUser = { id: string } | null

export interface ReportAdapter {
  send(event: ReportEvent): void
  setUser(user: ReportUser): void
  setTags(tags: Partial<Record<FwTag, string>>): void
  addBreadcrumb(crumb: Breadcrumb): void
}

export interface ErrorReporter {
  capture(value: unknown, context?: CaptureContext): void
  setUser(user: ReportUser): void
  setTags(tags: Partial<Record<FwTag, string>>): void
  addBreadcrumb(crumb: Breadcrumb): void
}

// drop: 버릴 에러는 어댑터에 가지 않는다(기본). tag: 버릴 에러도 보내되 fw.verdict 태그로 구분한다 —
// 비교 상대(Datadog)가 없을 때 "버리는 규칙이 중요한 에러를 삼키는지" 를 도구 안에서 확인하는 모드.
export type DropMode = 'drop' | 'tag'

export type CreateReporterOptions = {
  policy: ReportingPolicy
  adapters: ReportAdapter[]
  onDrop?: (error: AppError) => void
  debug?: boolean
  dropMode?: DropMode
  // tag 모드에서 verdict=drop 이벤트를 보낼 비율(0~1). 버릴 것은 양이 많아 쿼터를 지키려고 표본만 보낸다. send 는 전부.
  tagSampleRate?: number
  // 같은 지문의 network 실패를 페이지 세션당 몇 번까지 보낼지. 한 기기가 끊긴 채 자동 요청을 반복하면
  // 같은 이벤트가 수십 건씩 쌓여 쿼터를 녹인다. 판정이 아니라 쿼터 보호라 tag 모드에서도 적용한다. 없으면 제한 없음.
  // 결제는 양이 적고 먼저 통과시키는 원칙이라 빼고, tag 모드의 drop 표본은 세지 않는다(진짜 send 를 밀어내지 않게).
  networkRepeatLimit?: number
  random?: () => number
}

export const createReporter = ({
  policy,
  adapters,
  onDrop,
  debug = false,
  dropMode = 'drop',
  tagSampleRate = 1,
  networkRepeatLimit,
  random = Math.random,
}: CreateReporterOptions): ErrorReporter => {
  const sentNetworkCounts = new Map<string, number>()

  const isOverNetworkRepeatLimit = (
    error: AppError,
    key: string[] | undefined,
    dropped: boolean,
  ) => {
    if (networkRepeatLimit === undefined || error.kind !== 'network' || !key) return false
    if (error.flow === 'payment' || dropped) return false
    const id = key.join('|')
    const count = (sentNetworkCounts.get(id) ?? 0) + 1
    sentNetworkCounts.set(id, count)
    return count > networkRepeatLimit
  }

  // 보고 실패가 앱을 깨면 안 된다. 어댑터 예외는 여기서 끝난다.
  const safely = (label: string, run: () => void) => {
    try {
      run()
    } catch (adapterError) {
      if (debug) console.warn(`[error-reporting] ${label} failed`, adapterError)
    }
  }

  const each = (label: string, run: (adapter: ReportAdapter) => void) => {
    for (const adapter of adapters) safely(label, () => run(adapter))
  }

  return {
    // 전역 핸들러·ErrorBoundary 에 꽂히는 함수라 어떤 입력·정책·콜백이 와도 예외가 밖으로 새면 안 된다.
    capture(value, context = {}) {
      safely('capture', () => {
        const error = toAppError(value, {
          mechanism: context.mechanism,
          flow: context.flow,
          routeOverrides: policy.routeOverrides,
        })
        if (
          !error.flow &&
          error.route !== undefined &&
          policy.paymentRoutes.includes(error.route)
        ) {
          error.flow = 'payment'
        }
        error.origin = classify(error, policy)

        const dropped = shouldDrop(error, policy)
        if (dropped) {
          if (onDrop) safely('onDrop', () => onDrop(error))
          if (dropMode === 'drop') return
          // 취소는 판정이 자명하고 양이 많아(언마운트·쿼리 취소) tag 모드에서도 보내지 않는다
          if (error.code === CANCELED_CODE) return
          if (random() >= tagSampleRate) return
        }

        const key = fingerprint(error)
        if (isOverNetworkRepeatLimit(error, key, dropped)) return

        const verdict = dropMode === 'tag' ? (dropped ? 'drop' : 'send') : undefined
        const event = toReportEvent(error, key, verdict)
        each('send', (adapter) => adapter.send(event))
      })
    },
    setUser(user) {
      each('setUser', (adapter) => adapter.setUser(user))
    },
    setTags(tags) {
      each('setTags', (adapter) => adapter.setTags(tags))
    },
    addBreadcrumb(crumb) {
      each('addBreadcrumb', (adapter) => adapter.addBreadcrumb(crumb))
    },
  }
}
